import { html } from '@shared/utils/rendering/html';
import ActionsDropdown from '@shared/components/dropdowns/actions-dropdown';
import { useState, useEffect, useMemo, useRef } from 'preact/hooks';
import EvidenceComponents from './evidence-components';
import OrgFilterGroup from '../../shared/org-filters/org-filter-group';
import DashboardRatings from './dashboard-ratings';
import { useCachedFetch } from '../../../utils/hooks/use-cached-fetch';
import { useCache } from '../../../utils/context/cache-context';
import { CacheKeyNames } from '../../../constants/utility/cache-key-names';
import { ORG_FILTERS_STORAGE_KEY } from '../../../constants/utility/org-filters-storage.constants';

const EvidenceBasedApproach = () => {
    const setCache = useCache().setKey;
    const [validEntities, setValidEntities] = useState([]);
    const orgFiltersRestoredRef = useRef(false);

    const [orgFilters, setOrgFilters] = useState({
        legalEntity: null,
        period: null,
    });

    const [assessmentQueryOptions, setAssessmentQueryOptions] = useState({
        enabled: false,
    });

    const filters = useMemo(
        () => ({
            legalEntityId: orgFilters?.legalEntity?.value,
            periodId: orgFilters?.period?.value,
            periodValid: orgFilters?.period?.valid,
        }),
        [orgFilters?.legalEntity, orgFilters?.period]
    );

    useEffect(() => {
        const storedFilters = localStorage.getItem(ORG_FILTERS_STORAGE_KEY);
        if (storedFilters) {
            const parsedFilters = JSON.parse(storedFilters);
            if (parsedFilters.legalEntity?.value && validEntities?.length) {
                const isValidEntity = validEntities.find(
                    (e) => e.value === parsedFilters.legalEntity.value
                );
                if (!isValidEntity) {
                    parsedFilters.legalEntity = null;
                }
            }

            if (!parsedFilters?.validUntilDate || parsedFilters.validUntilDate < Date.now()) {
                setOrgFilters((prev) => ({
                    ...prev,
                    period: null,
                    legalEntity: null,
                }));
            } else {
                setOrgFilters(parsedFilters);
            }
        }
    }, []);

    useEffect(() => {
        if (!validEntities?.length) return;

        // When there is exactly one entity it must always be auto-selected.
        if (validEntities.length === 1) {
            setOrgFilters((prev) => ({ ...prev, legalEntity: validEntities[0] }));
            return;
        }

        if (orgFilters?.legalEntity?.value) {
            const isValidEntity = validEntities.find(
                (e) => e.value === orgFilters.legalEntity.value
            );
            if (!isValidEntity) {
                setOrgFilters((prev) => ({
                    ...prev,
                    legalEntity: null,
                }));
            }
        }
    }, [validEntities]);

    const {
        data: assessment,
        isLoading,
        refetch,
        isSuccess,
    } = useCachedFetch('/legal-entity-assessments', filters, assessmentQueryOptions);

    const refresh = () => {
        if (!isLoading && refetch) {
            refetch();
        }
    };

    useEffect(() => {
        if (filters?.legalEntityId && filters.periodId && !assessmentQueryOptions.enabled) {
            setAssessmentQueryOptions({ ...assessmentQueryOptions, enabled: true });
        }

        if ((!filters?.legalEntityId || !filters.periodId) && assessmentQueryOptions.enabled) {
            setAssessmentQueryOptions({ ...assessmentQueryOptions, enabled: false });
        }

        setCache(CacheKeyNames.legalEntityAssessmentData, {
            cacheKey: ['/legal-entity-assessments', filters],
        });
    }, [filters, assessmentQueryOptions]);

    // Skip the initial mount render so we don't overwrite a stored selection (shared with the
    // SARB tab) with the empty default before the read effect above restores it.
    useEffect(() => {
        if (!orgFiltersRestoredRef.current) {
            orgFiltersRestoredRef.current = true;
            return;
        }
        const validUntilDate = new Date();
        validUntilDate.setMonth(validUntilDate.getMonth() + 1);

        localStorage.setItem(
            ORG_FILTERS_STORAGE_KEY,
            JSON.stringify({ ...orgFilters, validUntilDate })
        );
    }, [orgFilters]);

    const orgFilterActions = html`<${ActionsDropdown} id="rdarr-actions-menu" disabled=${true} />`;

    return html`
        <${OrgFilterGroup}
            orgFilters=${orgFilters}
            setOrgFilters=${setOrgFilters}
            setValidEntities=${setValidEntities}
            actions=${orgFilterActions}
        />

        <${DashboardRatings} entityAssessment=${assessment} />

        <${EvidenceComponents}
            filters=${filters}
            isSuccess=${isSuccess}
            refresh=${refresh}
            isLoading=${isLoading}
            entityAssessment=${assessment}
        />
    `;
};

export default EvidenceBasedApproach;
